// ─────────────────────────────────────────────────────────────────────────────
// Keyboard shortcuts for v2 — accounting-software style navigation.
//
//   G then a key     → go to a page          (G I = Invoices, G ⇧R = Recurring invoices …)
//   N then a key     → create something      (N I = new invoice, N E = new expense …)
//   , then a key     → account               (, P = My profile, , B = Bank details, , A = Appearance …)
//   Alt + 1…9        → jump to rail section 1…9
//   A                → page's main "Add / New" action (primary button in the page header)
//   /                → focus the page's search field
//   J / K  (↓ / ↑)   → move through list rows; Enter opens the focused row
//   [ / ]            → previous / next period on month / quarter / year steppers
//   Esc              → back (detail pages) when nothing is open
//   Ctrl/⌘ + Enter   → save / confirm the open sheet (or the floating save bar)
//   Ctrl/⌘ + K       → search everything (existing command palette)
//   ?                → cheat sheet
// Keys are case-sensitive in sequences: "R" means Shift+R (shown as ⇧R).
// Pages can expose extra single-key actions with data-kbd="x" on a button (e.g. P = record payment).
// Single-key shortcuts never fire while typing in a field, or while a sheet/menu is open.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Sheet } from "./primitives";

// `module` → needs read permission; `admin` → owner/admin only; `flag` → only when that feature flag is on.
export const GO = [
  { k: "d", label: "Dashboard", to: "/dashboard/home", module: "dashboard" },
  // Receivables
  { k: "i", label: "Invoices", to: "/dashboard/viewinvoices", module: "invoices" },
  { k: "n", label: "Credit notes", to: "/dashboard/credit-notes", module: "invoices" },
  { k: "R", label: "Recurring invoices", to: "/dashboard/recurring-invoices", module: "invoices" },
  { k: "l", label: "Client ledger", to: "/dashboard/ledgerpage", module: "ledger" },
  { k: "t", label: "TDS receivable", to: "/dashboard/tdsreceivable", module: "tds" },
  { k: "c", label: "Clients", to: "/dashboard/manageclients", module: "clients" },
  // Payables
  { k: "b", label: "Bills & expenses", to: "/dashboard/bills", module: "bills" },
  { k: "p", label: "Purchase entry", to: "/dashboard/purchase-entry", module: "purchases" },
  { k: "o", label: "Purchase orders", to: "/dashboard/purchase-orders", module: "purchases" },
  { k: "D", label: "Debit notes", to: "/dashboard/debit-notes", module: "purchases" },
  { k: "s", label: "Suppliers", to: "/dashboard/managesuppliers", module: "suppliers" },
  { k: "u", label: "Supplier ledger", to: "/dashboard/supplier-ledger", module: "suppliers" },
  { k: "e", label: "Expenses", to: "/dashboard/expenses", module: "expenses" },
  // Inventory
  { k: "k", label: "Stock", to: "/dashboard/managestock", module: "stock" },
  { k: "I", label: "Import products", to: "/dashboard/importproducts", module: "stock" },
  // People
  { k: "h", label: "Staff", to: "/dashboard/staff", module: "staff" },
  { k: "a", label: "Attendance", to: "/dashboard/staff/attendance", module: "attendance" },
  { k: "y", label: "Payroll", to: "/dashboard/staff/payroll", module: "payroll" },
  // Sales team
  { k: "S", label: "Salespeople", to: "/dashboard/sales/salespeople", module: "sales" },
  { k: "C", label: "Commissions", to: "/dashboard/sales/commissions", module: "sales" },
  // Reports
  { k: "r", label: "Reports", to: "/dashboard/reports", module: "reports" },
  { k: "f", label: "Financial statements", to: "/dashboard/financial-reports", module: "reports" },
  { k: "g", label: "GST reports", to: "/dashboard/gst-reports", module: "gst" },
];
// `add: true` → after navigating, press the page's primary "Add" button (opens its create sheet).
export const NEW = [
  { k: "i", label: "Invoice", to: "/dashboard/createinvoice", module: "invoices" },
  { k: "n", label: "Credit note", to: "/dashboard/credit-notes", module: "invoices", add: true },
  { k: "r", label: "Recurring invoice", to: "/dashboard/recurring-invoices", module: "invoices", add: true },
  { k: "c", label: "Client", to: "/dashboard/manageclients", module: "clients", add: true },
  { k: "b", label: "Bill", to: "/dashboard/bills", module: "bills", add: true },
  { k: "e", label: "Expense", to: "/dashboard/expenses", module: "expenses", add: true },
  { k: "p", label: "Purchase entry", to: "/dashboard/purchase-entry", module: "purchases", add: true },
  { k: "o", label: "Purchase order", to: "/dashboard/purchase-orders", module: "purchases", add: true },
  { k: "d", label: "Debit note", to: "/dashboard/debit-notes", module: "purchases", add: true },
  { k: "s", label: "Supplier", to: "/dashboard/managesuppliers", module: "suppliers", add: true },
  { k: "k", label: "Product", to: "/dashboard/managestock", module: "stock", add: true },
  { k: "h", label: "Staff member", to: "/dashboard/staff", module: "staff", add: true },
  { k: "S", label: "Salesperson", to: "/dashboard/sales/salespeople", module: "sales", add: true },
  { k: "t", label: "Team member", to: "/dashboard/team", admin: true, add: true },
];
export const ACCOUNT = [
  { k: "p", label: "My profile", to: "/dashboard/profile" },
  { k: "b", label: "Bank details", to: "/dashboard/addbankdetails" },
  { k: "t", label: "Team", to: "/dashboard/team", admin: true },
  { k: "n", label: "Notifications", to: "/dashboard/notifications", flag: "notifications" },
  { k: "a", label: "Appearance", action: "appearance" },
];
const PREFIX = { g: { list: GO, title: "Go to…" }, n: { list: NEW, title: "Create new…" }, ",": { list: ACCOUNT, title: "Account…" } };

export const PAGE_KEYS = [
  ["A", "Add / New on this page"], ["/", "Search this page"], ["J  K", "Next / previous row"], ["Enter", "Open the focused row"],
  ["[  ]", "Previous / next period"], ["Esc", "Back from a detail page"], ["Ctrl Enter", "Save or confirm the open sheet"],
];
export const GLOBAL_KEYS = [["Ctrl K", "Search everything"], ["Alt 1–9", "Jump to a sidebar section"], ["?", "Show shortcuts"]];
export const keyLabel = (k) => (/^[A-Z]$/.test(k) ? `⇧${k}` : k.toUpperCase());
const hintFor = (list, prefix, to) => { const x = list.find((i) => i.to === to); return x ? `${prefix} ${keyLabel(x.k)}` : undefined; };
export const goHint = (to) => hintFor(GO, "G", to);
export const newHint = (to) => hintFor(NEW, "N", to);
export const accountHint = (to) => hintFor(ACCOUNT, ",", to);

const ROW_SEL = ":is(button, a):is(.v2cn-open, .v2i-open, .v2c-row, .v2l-row, .v2rp-acc-head)";
const typing = (el) => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
const overlayOpen = () => !!document.querySelector(".v2-layer .v2-sheet, .v2-glass-menu[data-state='open']");
const visible = (el) => el && el.offsetParent !== null && el.getClientRects().length > 0;
const firstVisible = (root, sel) => (root ? [...root.querySelectorAll(sel)].find(visible) : undefined);
const ADD_BTN = ".v2p-head .v2-btn--primary:not(:disabled)";

// Can the current user use this entry?
export const allowed = (x, { hasPermission, isAdmin, flags = {} }) =>
  (!x.module || hasPermission(x.module, "read")) && (!x.admin || !!isAdmin?.()) && (!x.flag || !!flags[x.flag]);

export function useShortcuts({ mainRef, navigate, pathname, hasPermission, isAdmin, flags, groups, actions = {}, sequences = true }) {
  const [pending, setPending] = useState(null);      // "g" | "n" | "," | null — shows the HUD
  const [help, setHelp] = useState(false);
  const pendingRef = useRef(null);
  const timer = useRef(0);
  const afterNav = useRef(null);                     // "add" → press the page's Add button once it renders
  const ctx = useRef({});
  ctx.current = { hasPermission, isAdmin, flags, actions };

  const setPend = (p) => {
    pendingRef.current = p; setPending(p);
    clearTimeout(timer.current);
    if (p) timer.current = setTimeout(() => { pendingRef.current = null; setPending(null); }, 2400);
  };

  // After "N X" navigation, wait for the page's primary Add button and press it.
  useEffect(() => {
    if (afterNav.current !== "add") return undefined;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      const btn = firstVisible(mainRef.current, ADD_BTN);
      if (btn) { btn.click(); afterNav.current = null; clearInterval(id); }
      else if (tries > 40) { afterNav.current = null; clearInterval(id); }
    }, 75);
    return () => clearInterval(id);
  }, [pathname, mainRef]);

  useEffect(() => {
    const run = (item) => {
      if (!allowed(item, ctx.current)) return;
      if (item.action) { ctx.current.actions[item.action]?.(); return; }
      if (item.add) {
        if (pathname === item.to) { firstVisible(mainRef.current, ADD_BTN)?.click(); return; }
        afterNav.current = "add";
      }
      navigate(item.to);
    };
    const moveRow = (dir) => {
      const rows = [...(mainRef.current?.querySelectorAll(ROW_SEL) || [])].filter(visible);
      if (!rows.length) return;
      const i = rows.indexOf(document.activeElement);
      const next = rows[i < 0 ? (dir > 0 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, i + dir))];
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };

    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key;
      // Ctrl/⌘ + Enter → save / confirm (works while typing — that's the point)
      if (mod && key === "Enter") {
        const sheets = [...document.querySelectorAll(".v2-layer .v2-sheet")];
        const top = sheets[sheets.length - 1];
        const btn = top ? [...top.querySelectorAll(".v2-sheet-foot :is(.v2-btn--primary, .v2c-btn-danger):not(:disabled)")].pop()
          : document.querySelector(".v2ac-savebar.is-on .v2-btn--primary:not(:disabled), .v2cm-bar.is-on .v2-btn--primary:not(:disabled)");
        if (btn) { e.preventDefault(); btn.click(); }
        return;
      }
      if (typing(e.target)) return;
      // Alt + 1…9 → rail sections
      if (e.altKey && !mod && /^Digit[1-9]$/.test(e.code)) {
        const g = groups[Number(e.code.slice(5)) - 1];
        if (g) { e.preventDefault(); navigate(g.to || g.items[0].to); }
        return;
      }
      if (mod || e.altKey) return;
      if (overlayOpen()) return;                       // sheets/menus own the keyboard
      // Sequence keys are case-sensitive: Shift+R → "R"; plain r → "r".
      const k = key.length === 1 ? (/[a-z]/i.test(key) ? (e.shiftKey ? key.toUpperCase() : key.toLowerCase()) : key) : key;
      const kl = key.length === 1 ? key.toLowerCase() : key;

      // Second key of a sequence
      if (pendingRef.current) {
        if (key === "Shift") return;                   // waiting for the shifted letter
        const pre = pendingRef.current;
        const hit = PREFIX[pre].list.find((x) => x.k === k);
        setPend(null);
        if (hit) { e.preventDefault(); run(hit); }
        return;
      }
      if (key === "?") { e.preventDefault(); setHelp(true); return; }
      if (sequences && !e.shiftKey && PREFIX[kl]) { e.preventDefault(); setPend(kl); return; }
      if (key === "/") {
        const f = firstVisible(mainRef.current, ".v2-field--search input, input[type='search']");
        if (f) { e.preventDefault(); f.focus(); f.select?.(); }
        return;
      }
      if (kl === "j" || (key === "ArrowDown" && document.activeElement?.matches?.(ROW_SEL))) { e.preventDefault(); moveRow(1); return; }
      if (kl === "k" || (key === "ArrowUp" && document.activeElement?.matches?.(ROW_SEL))) { e.preventDefault(); moveRow(-1); return; }
      if (key === "[" || key === "]") {
        const b = firstVisible(mainRef.current, key === "[" ? "button[aria-label^='Previous']:not(:disabled)" : "button[aria-label^='Next']:not(:disabled)");
        if (b) { e.preventDefault(); b.click(); }
        return;
      }
      if (key === "Escape") { const b = firstVisible(mainRef.current, ".v2l-back"); if (b) { e.preventDefault(); b.click(); } return; }
      // Page-declared single keys first (data-kbd), then A = add
      const own = firstVisible(mainRef.current, `[data-kbd="${CSS.escape(kl)}"]:not(:disabled)`);
      if (own) { e.preventDefault(); own.click(); return; }
      if (kl === "a") { const b = firstVisible(mainRef.current, ADD_BTN); if (b) { e.preventDefault(); b.click(); } }
    };
    // Capture phase: runs before sheets/menus react to the same key (e.g. Esc closing a sheet),
    // so "is an overlay open?" is answered for the moment the key was pressed.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [mainRef, navigate, pathname, groups, sequences]);

  useEffect(() => () => clearTimeout(timer.current), []);
  return { pending, cancel: () => setPend(null), help, setHelp };
}

const Keys = ({ keys }) => (
  <span className="v2-kbd-keys">{keys.split("  ").map((grp, i) => <span key={i} className="v2-kbd-combo">{grp.split(" ").map((t, j) => <kbd key={j}>{t}</kbd>)}</span>)}</span>
);

// Floating heads-up display after G, N or comma: shows what the next key does.
export function ShortcutHud({ pending, perms }) {
  const [shown, setShown] = useState(pending || "g");
  useEffect(() => { if (pending) setShown(pending); }, [pending]);
  const p = PREFIX[shown] || PREFIX.g;
  const list = p.list.filter((x) => allowed(x, perms));
  return createPortal(
    <div className={`v2-kbd-hud v2-layer${pending ? " is-on" : ""}`} role="status" aria-live="polite" aria-hidden={!pending}>
      <div className="v2-kbd-hud-title"><kbd>{shown === "," ? "," : shown.toUpperCase()}</kbd>{p.title}<span className="v2-kbd-hud-tip">⇧ = hold Shift</span></div>
      <div className="v2-kbd-hud-grid">{list.map((x) => <span key={x.k}><kbd>{keyLabel(x.k)}</kbd>{x.label}</span>)}</div>
    </div>, document.body);
}

export function ShortcutsSheet({ open, onClose, perms, pageOnly = false }) {
  const Row = ({ keys, label }) => <div className="v2-kbd-row"><span>{label}</span><Keys keys={keys} /></div>;
  const section = (title, sub, list, prefix, fmt = (x) => x.label) => (
    <><h4>{title} <span>{sub}</span></h4>{list.filter((x) => allowed(x, perms)).map((x) => <Row key={x.k} keys={`${prefix} ${keyLabel(x.k)}`} label={fmt(x)} />)}</>
  );
  return (
    <Sheet open={open} onClose={onClose} width={720} title="Keyboard shortcuts" subtitle="Press ? anywhere to see this again · ⇧ means hold Shift">
      <div className="v2-kbd-sheet">
        {!pageOnly && <section>{section("Go to", "press G, then…", GO, "G")}</section>}
        {!pageOnly && <section>
          {section("Create", "press N, then…", NEW, "N", (x) => `New ${x.label.toLowerCase()}`)}
          <h4 className="v2-kbd-gap">Account <span>press , then…</span></h4>
          {ACCOUNT.filter((x) => allowed(x, perms)).map((x) => <Row key={x.k} keys={`, ${keyLabel(x.k)}`} label={x.label} />)}
        </section>}
        <section>
          <h4>On any page</h4>{PAGE_KEYS.map(([k, l]) => <Row key={k} keys={k} label={l} />)}
          <h4 className="v2-kbd-gap">Everywhere</h4>{GLOBAL_KEYS.map(([k, l]) => <Row key={k} keys={k} label={l} />)}
          <h4 className="v2-kbd-gap">Open invoice</h4><Row keys="P" label="Record payment" /><Row keys="D" label="Download PDF" />
        </section>
      </div>
    </Sheet>
  );
}
